import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export type AnexoTipo = 'DAN' | 'BOL' | 'ROM' | 'CTE';

export interface ChaveNota {
  filOri: string;
  numero: string;
  serie: string;
  fornec: string;
  loja: string;
}

export interface Anexo {
  recno: number;
  tipo: AnexoTipo;
  filename: string;
  url: string;
  tamanho: number;
  hash: string;
  contentType: string;
  dataUpload: string; // YYYYMMDD
  hora: string;
  usuario: string;
}

export interface UploadAnexoPayload {
  chave: ChaveNota;
  tipo: AnexoTipo;
  filename: string;
  contentType: string;
  base64: string;
  gedFolder?: string;
  // CNAE do servico da nota. Usado pelo backend para resolver o
  // tpoCod/tpoDesDescricao no Conexos (mapping ArquiveiConfig).
  cnae?: string;
  // Vencimento da NF (ISO yyyy-mm-dd). Quando enviado, o backend usa
  // esta data para compor a pasta destino no SharePoint
  // (FINANCEIRO A PAGAR/<yyyy>/<mm.MES>/<RAIZ>/<dd>). Sem ele, cai
  // pra data de emissao da nota.
  dtVencimento?: string;
}

/** Item a ser lido antes do envio. `tag` identifica a origem na mensagem de erro (ex.: "NF 5458"). */
export interface AnexoParaPreparar {
  tipo: AnexoTipo;
  file: File;
  tag: string;
}

/** Anexo ja lido em memoria, pronto para o POST de upload. */
export interface AnexoPreparado {
  tipo: AnexoTipo;
  filename: string;
  contentType: string;
  base64: string;
}

export interface PreparoAnexosResult {
  preparados: AnexoPreparado[];
  erros: string[];
}

export interface AnexoDownload {
  filename: string;
  contentType: string;
  tamanho: number;
  base64: string;
}

interface ListResponse       { success: boolean; data: Anexo[]; }
interface DownloadResponse   { success: boolean; data: AnexoDownload; }
interface UploadResponse     { success: boolean; data: Anexo; }
interface OkResponse         { success: boolean; }
interface GedFoldersResponse { success: boolean; data: string[]; }
interface GedFolderExistsResponse { success: boolean; data: { exists: boolean; gedStatus?: GedStatus }; }

/**
 * Conclusividade da checagem de pasta GED, devolvida pelo backend.
 * 'OK'      - o Conexos respondeu; `exists` vale.
 * 'AUTH'    - login no Conexos recusado (credencial/sessao expirada).
 * 'OFFLINE' - Conexos nao respondeu a consulta.
 * Em AUTH/OFFLINE o `exists: false` NAO significa "pasta nao existe" - a
 * pasta pode estar la e o Protheus simplesmente nao conseguiu enxergar.
 */
export type GedStatus = 'OK' | 'AUTH' | 'OFFLINE';

export interface GedFolderCheck {
  exists: boolean;
  gedStatus: GedStatus;
}

/**
 * Mensagem pronta para o usuario quando a checagem nao foi conclusiva.
 * Retorna null quando gedStatus === 'OK' (ai a mensagem depende do fluxo).
 */
export function gedIndisponivelMsg(status: GedStatus): string | null {
  if (status === 'AUTH') {
    return 'Falha de autenticacao com o Conexos: o Protheus nao conseguiu entrar no GED ' +
           'para validar a pasta. As pastas podem existir normalmente. Acione o suporte ' +
           'para revalidar as credenciais de integracao antes de tentar de novo.';
  }
  if (status === 'OFFLINE') {
    return 'O GED do Conexos nao respondeu a consulta da pasta. As pastas podem existir ' +
           'normalmente. Tente novamente em alguns minutos; se persistir, acione o suporte.';
  }
  return null;
}

@Injectable({ providedIn: 'root' })
export class AnexosService {
  private readonly base = 'portalnf/anexos';
  static readonly MAX_BYTES = 10 * 1024 * 1024;

  constructor(private http: HttpClient) {}

  list(chave: ChaveNota): Observable<Anexo[]> {
    const params = new HttpParams()
      .set('filOri', chave.filOri)
      .set('doc',    chave.numero)   // wire: back espera 'doc' (= F1_DOC)
      .set('serie',  chave.serie)
      .set('fornec', chave.fornec)
      .set('loja',   chave.loja);
    return this.http.get<ListResponse>(this.base, { params }).pipe(
      map(r => r.data ?? [])
    );
  }

  upload(payload: UploadAnexoPayload): Observable<Anexo> {
    // Mapeia 'numero' (convencao do front) -> 'doc' (convencao do back / F1_DOC)
    const wire: any = {
      ...payload,
      chave: {
        filOri: payload.chave.filOri,
        doc:    payload.chave.numero,
        serie:  payload.chave.serie,
        fornec: payload.chave.fornec,
        loja:   payload.chave.loja
      }
    };
    if (payload.gedFolder)    { wire.gedFolder    = payload.gedFolder; }
    if (payload.cnae)         { wire.cnae         = payload.cnae; }
    if (payload.dtVencimento) { wire.dtVencimento = payload.dtVencimento; }
    return this.http.post<UploadResponse>(`${this.base}/upload`, wire).pipe(
      map(r => r.data)
    );
  }

  /**
   * Baixa o conteudo de um anexo (qualquer tipo) via streaming autenticado.
   * O backend le os bytes do GCP (bucket+objeto da ZOF) e devolve base64;
   * o componente monta um Blob e dispara o download. Substitui o antigo
   * window.open(url) que so funcionava p/ anexos com URL publica (DAN/CTE).
   */
  download(recno: number): Observable<AnexoDownload> {
    const params = new HttpParams().set('recno', String(recno));
    return this.http.get<DownloadResponse>(`${this.base}/download`, { params }).pipe(
      map(r => r.data)
    );
  }

  delete(recno: number): Observable<boolean> {
    const params = new HttpParams().set('recno', String(recno));
    return this.http.delete<OkResponse>(this.base, { params }).pipe(
      map(r => !!r.success)
    );
  }

  /**
   * Lista de pastas GED disponiveis (parseada do MV_PNFGFLD no backend).
   * Usado no dropdown "Pasta GED" do modal de upload.
   */
  gedFolders(): Observable<string[]> {
    return this.http.get<GedFoldersResponse>(`${this.base}/ged-folders`).pipe(
      map(r => r.data ?? [])
    );
  }

  /**
   * Pre-check: verifica se a pasta GED de destino dos anexos JA existe no
   * Conexos para o processo da nota. Usado para bloquear a aprovacao quando a
   * pasta ainda nao foi criada (evita anexo orfao / upload silenciosamente
   * falho). Sem processo o backend devolve exists=true (GED-por-processo nao
   * se aplica). Passe filOri (DS_FILIAL) OU cnpjFilial (fluxo notas-adicionar).
   */
  gedFolderExists(params: { filOri?: string; cnpjFilial?: string; processo: string; folder: string }): Observable<GedFolderCheck> {
    let hp = new HttpParams()
      .set('processo', params.processo ?? '')
      .set('folder',   params.folder   ?? '');
    if (params.filOri)     { hp = hp.set('filOri',     params.filOri); }
    if (params.cnpjFilial) { hp = hp.set('cnpjFilial', params.cnpjFilial); }
    return this.http.get<GedFolderExistsResponse>(`${this.base}/ged-folder-exists`, { params: hp }).pipe(
      // gedStatus ausente = backend antigo (pre-gedStatus): assume conclusivo
      // para nao mudar o comportamento durante a janela de deploy.
      map(r => ({
        exists:    r.data?.exists ?? false,
        gedStatus: r.data?.gedStatus ?? 'OK'
      }))
    );
  }

  /**
   * Le um File como base64 puro (sem o prefixo data:...;base64,).
   */
  fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const idx = result.indexOf(',');
        resolve(idx >= 0 ? result.slice(idx + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Le todos os anexos ANTES de qualquer efetivacao (aprovacao/envio).
   *
   * Motivo: o Chrome invalida o blob de um File quando o arquivo e movido,
   * renomeado ou reescrito no disco apos a selecao (OneDrive, antivirus,
   * re-salvamento), e o FileReader passa a rejeitar com NotFoundError. Lendo
   * antes de aprovar, a falha acontece enquanto a nota ainda esta pendente e
   * o operador pode reanexar sem perder nada.
   *
   * Nao propaga o texto cru da DOMException: a mensagem nomeia a nota, o
   * arquivo e a acao que resolve.
   */
  async prepararAnexos(itens: AnexoParaPreparar[]): Promise<PreparoAnexosResult> {
    const preparados: AnexoPreparado[] = [];
    const erros: string[] = [];

    for (const item of itens) {
      try {
        const base64 = await this.fileToBase64(item.file);
        preparados.push({
          tipo:        item.tipo,
          filename:    item.file.name,
          contentType: item.file.type || 'application/octet-stream',
          base64
        });
      } catch {
        erros.push(
          `${item.tag}: o arquivo "${item.file.name}" nao pode ser lido ` +
          `(pode ter sido movido, renomeado ou alterado apos a selecao). ` +
          `Reanexe o arquivo e tente novamente.`
        );
      }
    }

    return { preparados, erros };
  }
}
